import React, { useContext, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { useReadOnly } from 'hooks/read-only';


import { Stack } from 'ui/stack';

import Input from 'components/partials/input/input';

import { UserProfileContext } from 'containers/admin/users/user/user-profile/user-profile';
import {
  PERSONAL_INFO_FORM_FIELDS,
  PERSONAL_INFO_FORM_ALWAYS_EDITABLE_FIELDS,
} from 'containers/admin/users/user/forms/personal-info-form/personal-info-form-fields.constants';
import type {
  PersonalInfoFormField,
  PersonalInfoFormFieldMode,
} from 'containers/admin/users/user/forms/personal-info-form/personal-info-form-fields.constants';

interface PersonalInformationFormProps {
  isNotNewUser?: boolean;
  isFormDisabled?: boolean;
  isMVUser?: boolean;
}

const PersonalInformationForm = ({
  isNotNewUser,
  isFormDisabled,
  isMVUser,
}: PersonalInformationFormProps): React.JSX.Element => {
  const { control, errors, canOnlyEditJobTitle } =
    useContext(UserProfileContext);

  const [personalInfoFields, setPersonalInfoFields] = useState<
    PersonalInfoFormField[]
  >(PERSONAL_INFO_FORM_FIELDS);

  const readOnly = useReadOnly();

  const isSideBySide = !isFormDisabled && !isNotNewUser;

  const filterFormFieldsByMode = (
    filterKey: PersonalInfoFormFieldMode
  ): PersonalInfoFormField[] =>
    PERSONAL_INFO_FORM_FIELDS.filter((field) => field.mode === filterKey);

  const calculateFormFieldDisability = (
    fields: PersonalInfoFormField[]
  ): PersonalInfoFormField[] =>
    fields.map((field) => {
      const isEditable = canOnlyEditJobTitle
        ? field.name === 'jobTitle'
        : PERSONAL_INFO_FORM_ALWAYS_EDITABLE_FIELDS.has(field.name);
      const disabled = !isEditable && (readOnly || canOnlyEditJobTitle);

      return { ...field, disabled };
    });

  useEffect(() => {
    if (!isNotNewUser) {
      return;
    }

    const fields = calculateFormFieldDisability(
      filterFormFieldsByMode('CREATE_AND_EDIT_MODE')
    );

    setPersonalInfoFields(fields);
  }, [isNotNewUser, canOnlyEditJobTitle]);

  return (
    <Stack
      sx={[
        !!isFormDisabled && { width: '100%' },
        isSideBySide && { width: '50%', mr: 8 },
      ]}
    >
      {personalInfoFields.map(
        ({ label, name, key, renderComponentProps, disabled, isOptional }) => (
          <Controller
            key={key}
            name={name}
            control={control as never}
            render={({ field }) => (
              <Input
                customClass="w-36"
                errorsNames={errors[name]}
                label={label}
                labelFor={name}
                labelInTop
                inputWrapperCustomClass={'bg-gray-100'}
                disabled={isFormDisabled || disabled}
                disabledClassName={
                  ((isFormDisabled || readOnly) && 'bg-gray-100') || undefined
                }
                required={
                  !isOptional &&
                  (isMVUser ? !isFormDisabled && !disabled : !isFormDisabled)
                }
                {...field}
                {...renderComponentProps}
              />
            )}
          />
        )
      )}
    </Stack>
  );
};

export default PersonalInformationForm;
